import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterNegotiationService } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { useCollectionResponsibleOptions } from "../../hooks/useCollectionResponsibleOptions";
import { SinisterSector } from "../../constants/sectorOptions";
import { FollowUpGenerationOptions } from "../FollowUpGenerationOptions";
import { generateFollowUps } from "../../utils/generateFollowUps";

type ResponsibleOption = { id: string; label: string };

interface Props {
	open: boolean;
	sinisterId: string;
	sector: SinisterSector;
	currentResponsibleId?: string | null;
	currentResponsibleName?: string | null;
	onClose: () => void;
	onConfirm?: (responsible: { id: string; name: string }) => void;
}

export const ModalChangeResponsible = ({ open, sinisterId, sector, currentResponsibleId, currentResponsibleName, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [search, setSearch] = useState("");
	const [selected, setSelected] = useState<ResponsibleOption | null>(null);
	const [generateFollowUp, setGenerateFollowUp] = useState(true);
	// Follow up externo é visível pro sinistro e pra imobiliária (e dispara e-mail aos contatos
	// cadastrados) — por isso começa desmarcado, é uma escolha explícita de cada lançamento.
	const [generateExternalFollowUp, setGenerateExternalFollowUp] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { responsibleOptions, loading } = useCollectionResponsibleOptions(sector, search);

	useEffect(() => {
		if (open) {
			setSearch("");
			setSelected(
				currentResponsibleId && currentResponsibleName ? { id: currentResponsibleId, label: currentResponsibleName } : null
			);
			setGenerateFollowUp(true);
			setGenerateExternalFollowUp(false);
		}
	}, [open, currentResponsibleId, currentResponsibleName]);

	const handleConfirm = async () => {
		if (!selected) return;

		setIsSubmitting(true);
		const result = await trackAsync(
			"negotiation.update_collection_responsible",
			() => SinisterNegotiationService.updateCollectionResponsible(sinisterId, selected.id),
			{ sinisterId }
		);

		if (result instanceof Error) {
			setIsSubmitting(false);
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		// Ação secundária: se falhar, não deve derrubar o sucesso da ação principal (responsável já
		// foi salvo). Endpoint ainda não existe no backend — pedido formalizado no gap doc.
		const notifyResult = await trackAsync(
			"negotiation.notify_collection_responsible",
			() => SinisterNegotiationService.notifyCollectionResponsible(sinisterId),
			{ sinisterId }
		);
		if (notifyResult instanceof Error) {
			console.error("Falha ao notificar o responsável pela cobrança por e-mail:", notifyResult.message);
			toastHandler.present({
				type: "error",
				messageOrContent: `Responsável atualizado, mas a notificação por e-mail falhou: ${notifyResult.message}`,
				position: "top-right",
			});
		}

		await generateFollowUps({
			sinisterId,
			note: `<p>Responsável pela cobrança atualizado para: ${selected.label}</p>`,
			generateInternal: generateFollowUp,
			generateExternal: generateExternalFollowUp,
			trackLabel: "update_collection_responsible",
		});

		setIsSubmitting(false);
		onConfirm?.({ id: selected.id, name: selected.label });
		toastHandler.present({
			type: "success",
			messageOrContent: `Responsável pela cobrança atualizado para ${selected.label}.`,
			position: "top-right",
		});
		onClose();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Alterar Responsável
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Typography variant="body2" color="text.secondary">
							Selecione o responsável pela atuação de cobrança deste sinistro.
						</Typography>

						<Autocomplete
							options={responsibleOptions}
							value={selected}
							loading={loading}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							getOptionLabel={(option) => option.label}
							onChange={(_, value) => setSelected(value)}
							onInputChange={(_, value, reason) => {
								if (reason === "input") setSearch(value);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Responsável"
									placeholder="Digite o nome do responsável"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loading ? <CircularProgress size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>

						<FollowUpGenerationOptions
							generateInternal={generateFollowUp}
							onChangeInternal={setGenerateFollowUp}
							generateExternal={generateExternalFollowUp}
							onChangeExternal={setGenerateExternalFollowUp}
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Cancelar</Button>
					<Button variant="contained" disabled={isSubmitting || !selected || selected.id === currentResponsibleId} onClick={handleConfirm}>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
